import React from 'react'
import { IUser } from 'kach-clients'
import { buildUserName } from '../utils/build-user-name'
import {
  Tooltip,
  TooltipProvider,
  TooltipContent,
  TooltipTrigger,
} from 'ui/components/tooltip'
import { Avatar, AvatarFallback, AvatarImage } from 'ui/components/avatar'
import { cn } from 'ui/lib/utils'

const avatarSizeMap = {
  '2xs': 'h-4 w-4',
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
  xl: 'h-16 w-16',
  '2xl': 'h-20 w-20',
}

const textSizeMap = {
  '2xs': 'text-xs',
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
}

export const UserDisplayerSCN: React.FC<
  React.PropsWithChildren<{
    fullWidth?: boolean
    user: Pick<
      IUser,
      | 'firstName'
      | 'lastName'
      | 'email'
      | 'profilePhoto'
      | 'cellphone'
      | 'deletedAt'
    >
    hideEmail?: boolean
    avatarSize?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    textSize?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    title?: string
    withContactCellphone?: boolean
    childrenPosition?: 'bottom' | 'right'
    /** Shown on the name's line, right after it. Nothing when absent. */
    nameAddon?: React.ReactNode
  }>
> = ({ childrenPosition, avatarSize = 'sm', textSize = 'sm', ...props }) => {
  return (
    <div
      {...(props.fullWidth ? { width: '100%' } : {})}
      className={cn('flex items-center gap-2 cursor-default')}
      color={props.user?.deletedAt ? 'gray.400' : 'inherit'}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Avatar
              className={cn(
                avatarSizeMap[avatarSize],
                'rounded-md cursor-default',
              )}
            >
              <AvatarImage
                src={props.user.profilePhoto}
                alt={props.user.firstName}
                className='rounded-md'
                style={{
                  filter: props.user?.deletedAt ? 'grayscale(100%)' : 'none',
                }}
              />
              <AvatarFallback className='rounded-md cursor-default'>
                {props.user?.deletedAt
                  ? 'Usuario inactivo'
                  : `${props.user?.firstName?.charAt(
                      0,
                    )}${props.user?.lastName?.charAt(0)}`}
              </AvatarFallback>
            </Avatar>
          </TooltipTrigger>
          <TooltipContent side='left'>
            {props.user?.deletedAt
              ? 'Usuario inactivo'
              : buildUserName(props.user)}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <div className='grid grid-cols-1'>
        {props.title ? <span className='text-xs'>{props.title}</span> : null}
        {props.nameAddon ? (
          <div className='flex items-center gap-1'>
            <span
              className={cn(
                'font-semibold text-primary',
                textSizeMap[textSize],
              )}
            >
              {buildUserName(props.user)}
            </span>
            <span className='shrink-0 whitespace-nowrap'>
              {props.nameAddon}
            </span>
          </div>
        ) : (
          <span
            className={cn('font-semibold text-primary', textSizeMap[textSize])}
          >
            {buildUserName(props.user)}
          </span>
        )}
        {!props.hideEmail ? (
          <p
            title={props.user ? `${props.user.email}` : ''}
            className='text-gray-400 text-xs truncate'
          >
            {props.user ? `${props.user.email}` : ''}
          </p>
        ) : null}

        {props.withContactCellphone && !!props.user.cellphone && (
          <span className='text-gray-400 text-xs'>{props.user.cellphone}</span>
        )}

        {childrenPosition && childrenPosition === 'bottom' && (
          <div className='m-1'>{props.children}</div>
        )}
      </div>
      {childrenPosition && childrenPosition === 'right' && (
        <div className='m-1'>{props.children}</div>
      )}
    </div>
  )
}
